<template>
  <div ref="rootEl" class="mt">
    <!-- Rails -->
    <div class="mt-rail mt-rail--base" aria-hidden="true"></div>
    <div class="mt-rail mt-rail--progress" aria-hidden="true"></div>
    <div class="mt-rail mt-rail--complete" aria-hidden="true"></div>

    <div v-for="(item, index) in items" :key="item.id ?? index" class="mt-row">
      <div class="mt-dotcol" aria-hidden="true">
        <span
          class="mt-dot"
          :class="dotClass(item.status)"
          :ref="(el) => setDotRef(el, index)"
        />
      </div>

      <div class="mt-content">
        <div class="mt-pill-wrap">
          <span class="status-pill" :class="pillClass(item.status)">
            {{ labelForStatus(item.status) }}
          </span>
        </div>

        <h3 class="mt-title">{{ item.title }}</h3>
        <p class="mt-date" v-if="item.completedAt">Completed: {{ new Date(item.completedAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) }} </p>
        <p class="mt-date" v-if="item.dueAt">Due: {{ new Date(item.dueAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) }} </p>

        <p v-if="item.description" class="mt-desc">
          {{ item.description }}
        </p>
      </div>
    </div>

    <!-- tail spacer so the rail can extend below the last description -->
    <div class="mt-tail" aria-hidden="true"></div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"

const props = defineProps({
  items: { type: Array, default: () => [] }
})

function labelForStatus(status) {
  switch (status) {
    case "completed":
      return "Complete"
    case "current":
      return "In Progress"
    case "overdue":
      return "Overdue"
    default:
      return "Not Started"
  }
}

function pillClass(status) {
  switch (status) {
    case "completed":
      return "complete"
    case "current":
      return "in-progress"
    case "overdue":
      return "overdue"
    default:
      return "not-started"
  }
}

function dotClass(status) {
  return {
    "is-complete": status === "completed",
    "is-progress": status === "current",
    "is-overdue": status === "overdue",
    "is-not-started": !["completed", "current", "overdue"].includes(status)
  }
}

const lastCompletedIndex = computed(() => {
  let idx = -1
  props.items.forEach((it, i) => {
    if (it?.status === "completed") idx = i
  })
  return idx
})

const currentIndex = computed(() => props.items.findIndex((it) => it?.status === "current"))

const rootEl = ref(null)
const dotEls = ref([])

function setDotRef(el, index) {
  if (!el) return
  dotEls.value[index] = el
}

let ro = null

function measureAndSetRails() {
  const root = rootEl.value
  if (!root) return

  const dots = dotEls.value.filter(Boolean)
  if (!dots.length) return

  const rootRect = root.getBoundingClientRect()

  const centerY = (el) => {
    const r = el.getBoundingClientRect()
    return (r.top - rootRect.top) + r.height / 2
  }

  const firstY = centerY(dots[0])
  const lastY = centerY(dots[dots.length - 1])

  const currentEl = currentIndex.value >= 0 ? dotEls.value[currentIndex.value] : null
  const completedEl = lastCompletedIndex.value >= 0 ? dotEls.value[lastCompletedIndex.value] : null

  const currentY = currentEl ? centerY(currentEl) : null
  const completedY = completedEl ? centerY(completedEl) : null

  const lead = 46
  const tail = 220

  const railTop = Math.max(0, firstY - lead)
  const railBottom = Math.max(0, rootRect.height - (lastY + tail))

  root.style.setProperty("--rail-top", `${railTop}px`)
  root.style.setProperty("--rail-bottom", `${railBottom}px`)

  const isLastMilestoneCompleted =
    props.items.length > 0 &&
    lastCompletedIndex.value === props.items.length - 1

  const fullRailHeight = Math.max(0, rootRect.height - railBottom - railTop)

  let completeHeight = 0

  if (isLastMilestoneCompleted) {
    completeHeight = fullRailHeight
  } else {
    const greenEndY = currentY != null ? currentY : completedY
    completeHeight = greenEndY == null ? 0 : Math.max(0, greenEndY - railTop)
  }

  root.style.setProperty("--complete-h", `${completeHeight}px`)
  root.style.setProperty("--progress-h", `0px`)
}

async function refresh() {
  await nextTick()
  requestAnimationFrame(measureAndSetRails)
}

onMounted(async () => {
  await refresh()
  ro = new ResizeObserver(measureAndSetRails)
  if (rootEl.value) ro.observe(rootEl.value)
})

onBeforeUnmount(() => {
  if (ro && rootEl.value) ro.unobserve(rootEl.value)
  ro = null
})

watch(
  () => props.items,
  async () => {
    dotEls.value = []
    await refresh()
  },
  { deep: true }
)

watch([currentIndex, lastCompletedIndex], refresh)
</script>

<style scoped>
.mt {
  --rail-top: 0px;
  --rail-bottom: 0px;
  --progress-h: 0px;
  --complete-h: 0px;

  --pad-x: 12px;
  --dotcol-w: 28px;

  --rail-w: 6px;
  --dot: 16px;

  --rail-base: rgba(232, 214, 255, 0.65);
  --pale-pink: #f3e6f8;

  position: relative;
  width: 100%;
  padding: 16px var(--pad-x);
}

.mt-row {
  display: grid;
  grid-template-columns: var(--dotcol-w) 1fr;
  column-gap: 16px;
  align-items: start;
  padding: 22px 0;
}

.mt-dotcol {
  position: relative;
  width: var(--dotcol-w);
}

/* rails */
.mt-rail {
  position: absolute;
  left: calc(var(--pad-x) + (var(--dotcol-w) / 2) - (var(--rail-w) / 2));
  top: var(--rail-top);
  bottom: var(--rail-bottom);
  width: var(--rail-w);
  border-radius: 999px;
  pointer-events: none;
}

.mt-rail--base {
  background: var(--rail-base);
  z-index: 0;
}

/* (kept, but height is set to 0 in JS for your requested behaviour) */
.mt-rail--progress {
  height: var(--progress-h);
  background: var(--mauve);
  z-index: 1;
}

.mt-rail--complete {
  height: var(--complete-h);
  background: var(--green);
  z-index: 2;
}

/* dot */
.mt-dot {
  position: absolute;
  left: 50%;
  top: 10px;
  transform: translateX(-50%);
  width: var(--dot);
  height: var(--dot);
  border-radius: 999px;
  z-index: 3;
}

.mt-dot.is-complete {
  background: var(--blue);
}
.mt-dot.is-progress {
  background: var(--mauve);
}
.mt-dot.is-not-started {
  background: var(--pale-pink);
}
.mt-dot.is-overdue {
  background: var(--red);
}

/* content */
.mt-content {
  max-width: 620px;
}

.mt-pill-wrap {
  margin-left: -8px;
  margin-bottom: 20px;
}

.mt-title {
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 800;
  color: var(--green);
}

.mt-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #5f6c72;
}

.mt-date {
  margin: 2px 0 12px 0;
  font-size: 16px;
  line-height: 1.6;
  color: var(--green);
}

/* extra space at bottom so rail can extend much further */
.mt-tail {
  height: 220px;
}

@media (max-width: 600px) {
  .mt {
    --pad-x: 10px;
    --dotcol-w: 24px;
    padding: 14px var(--pad-x);
  }

  .mt-row {
    grid-template-columns: var(--dotcol-w) 1fr;
    column-gap: 12px;
  }

  .mt-content {
    max-width: 100%;
  }
}
</style>